import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { autoCalibrate, getPlayingSegments } from "refsix-core";
import {
    GpsCalibrationPoints,
    GpsPoint,
    MatchPhone,
    PitchCalibrationMethod,
    Sprint,
} from "refsix-js-models";
import {
    CardMapComponent,
    HeatMapComponent,
    OverlayMapComponent,
    SprintMapComponent,
} from ".";
import { useFitnessDataContext } from "./hooks";
import { UserProfile } from "../models/userProfile";
import { RootState } from "../redux/store";
import { setCalibrationPoints as saveCalibrationPoints } from "../services/fitnessDataSync/fitnessDataSyncService";
import { recalculateStats } from "../services/matchService";
import pitchImage from "../assets/images/pitch.png";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { IonButton, IonIcon, IonText } from "@ionic/react";
import { CalibrationModal } from "../pages/tabs/fixturesSegments/calibration/calibrationModal";
import exampleMatch from "../support/example-result.json";
import "./GpsMapComponent.css";
import {
    calibrationCompleted,
    calibrationStarted,
    trackEvent,
} from "../services/analytics/analyticsService";
import { CompareHeatmapsModal } from "../modals/compareHeatmapsModal";
import { sparkles, swapHorizontal } from "ionicons/icons";

export enum GpsMapType {
    Heatmap,
    SprintMap,
    CardMap,
    recalibrate,
    Compare,
}

interface GpsMapProps {
    match: MatchPhone;
    mapType: GpsMapType;
    sprintsData?: Sprint<number>[];
    testId?: string;
    useDummyData?: boolean;
}

// Auto-calibration (REFSIX-669) is attempted at most once per match across ALL
// GpsMap instances. The stats page renders three at once (heatmap/sprintmap/
// cardmap), and three concurrent setCalibrationPoints writes to the same GPS doc
// deadlock the PouchDB upsert+sync — so this guard is module-scoped (shared
// between instances), not per-component.
const autoCalibrationStarted = new Set<string>();

export const GpsMap: FC<GpsMapProps> = ({
    match,
    mapType,
    sprintsData,
    testId,
    useDummyData,
}) => {
    const userProfile = useSelector(
        (state: RootState) => state.currentUser?.profile
    ) as UserProfile;

    const newMatch = useDummyData
        ? (exampleMatch.match as unknown as MatchPhone)
        : match;

    const [geoPoints, setGeoPoints] = useState<GpsPoint[]>();
    const [calibrationPoints, setCalibrationPoints] =
        useState<GpsCalibrationPoints>();
    const [showCalibrationModal, setShowCalibrationModal] = useState(false);

    const playedSegments =
        (newMatch.matchEvents && getPlayingSegments(newMatch)) || [];
    const [showCompareHeatMapsModal, setShowCompareHeatMapsModal] =
        useState(false);

    const { gpsData, loading, reload } = useFitnessDataContext();

    // Read by the auto-calibration callbacks, which must not re-run the effect
    // when these change; the effect's deps are chosen deliberately below.
    const latest = useRef({ mapType, newMatch, userProfile });
    latest.current = { mapType, newMatch, userProfile };

    useEffect(() => {
        if (useDummyData) {
            setGeoPoints(exampleMatch.gps.geoPoints);
            setCalibrationPoints(
                exampleMatch.gps.calibrationPoints as GpsCalibrationPoints
            );
            return;
        }
        if (!gpsData || !gpsData.geoPoints) {
            return;
        }
        setGeoPoints(gpsData.geoPoints);

        if (gpsData.calibrationPoints) {
            setCalibrationPoints(gpsData.calibrationPoints);
            return;
        }

        // No stored calibration — auto-calibrate from the GPS track (REFSIX-669)
        // so the heatmap/sprintmap/cardmap render on first view without the
        // manual 3-tap flow. If the model can't get a viable box (guards fail)
        // it resolves null and the manual calibration overlay stays as fallback.
        const matchId = newMatch._id;
        if (!matchId || !userProfile?.username) {
            return;
        }
        if (autoCalibrationStarted.has(matchId)) {
            return;
        }
        autoCalibrationStarted.add(matchId);

        // The guard entry stays latched only for attempts that reach a real
        // outcome (the model declined the track, or a save was started). A
        // cancelled or crashed attempt unlatches so a later mount can retry —
        // otherwise an unmount mid-flight (incl. StrictMode's dev remount)
        // would silently disable auto-calibration for the rest of the session.
        let cancelled = false;
        let saveStarted = false;
        let declined = false;
        autoCalibrate(gpsData.geoPoints)
            .then((points) => {
                if (!points) {
                    // Deterministic "track can't be boxed" — keep the latch so
                    // we don't re-run the model; the manual CTA is the fallback.
                    declined = true;
                    return;
                }
                if (cancelled) {
                    return; // unlatched by the cleanup below
                }
                setCalibrationPoints(points);
                const { mapType, newMatch, userProfile } = latest.current;
                // The calibration genuinely completed on this device, so it is
                // tracked here (REFSIX-806). Points that arrive already
                // calibrated from the server are deliberately NOT tracked — the
                // event fired on whichever device ran the model.
                calibrationCompleted(
                    mapType.toString(),
                    newMatch,
                    points.calibratedBy ?? PitchCalibrationMethod.PointNet
                );
                saveStarted = true;
                saveCalibrationPoints(matchId, userProfile.username, points)
                    .then(() => recalculateStats(newMatch, userProfile))
                    .catch((err) => {
                        // Persisting is transient-failure-prone (network) —
                        // unlatch so the next view of this match retries.
                        autoCalibrationStarted.delete(matchId);
                        console.warn(
                            "auto-calibration: persisting points failed",
                            err
                        );
                    });
            })
            .catch((err) => {
                autoCalibrationStarted.delete(matchId);
                console.warn("auto-calibration failed", err);
            });

        return () => {
            cancelled = true;
            // Unlatch only attempts that never reached an outcome; a declined
            // track or a started save keeps the latch (once per session).
            if (!saveStarted && !declined) {
                autoCalibrationStarted.delete(matchId);
            }
        };
        // newMatch._id is a dep so a match switch on a reused component
        // instance cancels the in-flight attempt instead of persisting the old
        // track's calibration under the new match's id.
    }, [gpsData, userProfile?.username, newMatch._id, useDummyData]);

    useEffect(() => {
        ///reload();
    }, [match.stats, reload]);

    const handleCornersSubmitted = (points: GpsCalibrationPoints) => {
        setShowCalibrationModal(false);

        if (newMatch._id) {
            saveCalibrationPoints(
                newMatch._id,
                userProfile.username,
                points
            ).then(() => recalculateStats(newMatch, userProfile));
        }
    };

    const renderMap = () => {
        if (
            (mapType === GpsMapType.Heatmap ||
                mapType === GpsMapType.Compare) &&
            hasAllDataForHeatmap()
        ) {
            return (
                <HeatMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    periodsNo={newMatch.periodsNo as string}
                    testId={testId}
                />
            );
        }
        if (mapType === GpsMapType.SprintMap && hasAllDataForSprintMap()) {
            return (
                <SprintMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    periodsNo={newMatch.periodsNo as string}
                    sprints={sprintsData as Sprint<number>[]}
                    match={newMatch}
                    testId={testId}
                />
            );
        }
        if (mapType === GpsMapType.CardMap && hasAllDataForCardMap()) {
            return (
                <CardMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    match={newMatch}
                    testId={testId}
                />
            );
        }
    };

    const renderCalibrationOverlay = () => {
        const title =
            mapType === GpsMapType.Heatmap || mapType === GpsMapType.Compare
                ? t("fixture.FixtureSummaryStats.viewHeatmap")
                : mapType === GpsMapType.CardMap
                ? t("fixture.FixtureSummaryStats.viewCardmap")
                : t("fixture.FixtureSummaryStats.viewSprintmap");
        return (
            <OverlayMapComponent
                imageUrl={pitchImage}
                title={title}
                showButton={true}
                onButtonClick={() => {
                    setShowCalibrationModal(true);
                    calibrationStarted(mapType.toString(), match);
                }}
                btnTestId="heatMapOverlayBtn"
            />
        );
    };
    const renderNoGpsDataOverlay = () => (
        <OverlayMapComponent
            imageUrl={pitchImage}
            title={t("fixture.FixtureSummaryStats.errorCode2")}
        />
    );

    const hasNoGpsData = () => !useDummyData && !gpsData && !loading;

    // REFSIX-806: the provenance tag is persisted with the GPS file, so this is
    // true both when the model just ran on this device and when already-tagged
    // points were downloaded from the server.
    const isAiCalibrated =
        !!calibrationPoints &&
        calibrationPoints.calibratedBy !== undefined &&
        calibrationPoints.calibratedBy !== PitchCalibrationMethod.Human;

    const hasAllDataForHeatmap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo
        );
    const hasAllDataForSprintMap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo &&
            sprintsData
        );
    const hasAllDataForCardMap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo &&
            newMatch.matchEvents
        );
    const hasAllDataForMap = () =>
        ((mapType === GpsMapType.Heatmap || mapType === GpsMapType.Compare) &&
            hasAllDataForHeatmap()) ||
        (mapType === GpsMapType.SprintMap && hasAllDataForSprintMap()) ||
        (mapType === GpsMapType.CardMap && hasAllDataForCardMap());

    return (
        <div
            className={
                hasNoGpsData()
                    ? "gps-map no-gps-data"
                    : hasAllDataForMap()
                    ? "gps-map "
                    : "gps-map needs-calibration"
            }
        >
            {hasNoGpsData()
                ? renderNoGpsDataOverlay()
                : hasAllDataForMap()
                ? renderMap()
                : renderCalibrationOverlay()}
            {calibrationPoints && mapType !== GpsMapType.Compare && (
                <>
                    {/* Provenance caption (REFSIX-806) and the adjust link share
                        one row: caption hard left, link hard right. The caption
                        slot is always rendered so the link stays right-aligned
                        for human-calibrated pitches too. */}
                    <div className="recalibrate-item item-no-padding-end">
                        {isAiCalibrated && (
                            <div
                                className="calibrated-by-ai"
                                data-testid="calibrated-by-ai"
                            >
                                <IonIcon icon={sparkles} />
                                <IonText>
                                    <Trans
                                        i18nKey="fixture.FixtureSummaryStats.calibratedByAi"
                                        components={{
                                            b: (
                                                <span className="rainbox-text" />
                                            ),
                                        }}
                                    />
                                </IonText>
                            </div>
                        )}

                        <IonButton
                            fill="clear"
                            size="small"
                            data-testid="adjust-pitch-button"
                            onClick={() => {
                                mapType = GpsMapType.recalibrate;
                                setShowCalibrationModal(true);
                                calibrationStarted(mapType.toString(), match);
                            }}
                        >
                            <IonText className="recalibrate">
                                {t("fixture.FixtureSummaryStats.adjustPitch")}
                            </IonText>
                        </IonButton>
                    </div>

                    {mapType === GpsMapType.Heatmap && (
                        <div className="compare-item">
                            <IonButton
                                size="small"
                                data-testid="compare-heatmaps-button"
                                onClick={() => {
                                    setShowCompareHeatMapsModal(true);
                                    trackEvent("HeatmapCompare", {});
                                }}
                            >
                                <IonIcon icon={swapHorizontal} slot={"start"} />
                                {t(
                                    "fixture.FixtureSummaryStats.compareHeatMaps"
                                )}
                            </IonButton>
                        </div>
                    )}
                </>
            )}

            <CompareHeatmapsModal
                showModal={showCompareHeatMapsModal}
                match={match}
                onCloseModal={() => setShowCompareHeatMapsModal(false)}
            />

            <CalibrationModal
                display={showCalibrationModal}
                gpsPoints={geoPoints || []}
                onCornersSubmited={(points) => {
                    calibrationCompleted(
                        mapType.toString(),
                        match,
                        PitchCalibrationMethod.Human
                    );
                    handleCornersSubmitted(points);
                }}
                onCloseModal={() => setShowCalibrationModal(false)}
            />
        </div>
    );
};
